import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Heart, Quote, Sparkles, Sun, Moon, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const moods = [
  { key: 'great', label: 'Great', emoji: '🔥', color: 'from-emerald-500 to-green-600', bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
  { key: 'good', label: 'Good', emoji: '😊', color: 'from-blue-500 to-cyan-600', bg: 'bg-blue-500/10', text: 'text-blue-400' },
  { key: 'okay', label: 'Okay', emoji: '😐', color: 'from-amber-500 to-yellow-600', bg: 'bg-amber-500/10', text: 'text-amber-400' },
  { key: 'stressed', label: 'Stressed', emoji: '😰', color: 'from-orange-500 to-red-600', bg: 'bg-orange-500/10', text: 'text-orange-400' },
  { key: 'bad', label: 'Bad', emoji: '😞', color: 'from-red-500 to-rose-600', bg: 'bg-red-500/10', text: 'text-red-400' },
];

// Check if current time is within mood check-in windows
// Morning: 9am - 1pm, Afternoon: 2pm - 5pm
const getMoodPeriod = () => {
  const hour = new Date().getHours();
  if (hour >= 9 && hour < 13) return 'morning';
  if (hour >= 14 && hour < 17) return 'afternoon';
  return null; // Outside check-in windows
};

export function DailyPopups() {
  const [showMoodPopup, setShowMoodPopup] = useState(false);
  const [showQuotePopup, setShowQuotePopup] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(null);
  const [selectedMood, setSelectedMood] = useState(null);
  const [moodNote, setMoodNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [moodPeriod, setMoodPeriod] = useState(getMoodPeriod());
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    checkMoodStatus();
  }, []);

  const checkMoodStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const period = getMoodPeriod();
      
      // Outside check-in windows (before 9am, 1pm-2pm, after 5pm) - don't show popup
      if (!period) {
        setInitialized(true);
        return;
      }
      
      setMoodPeriod(period);

      // Check if user has submitted mood for current period
      const moodRes = await fetch(`${API}/mood/status?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (moodRes.ok) {
        const moodData = await moodRes.json();
        
        // If not submitted for this period, show mood popup
        if (!moodData.submitted) {
          setShowMoodPopup(true);
        } else {
          // Mood submitted, show quote popup
          fetchAndShowQuote();
        }
      }
      setInitialized(true);
    } catch (err) {
      console.error('Error checking mood status:', err);
      setInitialized(true);
    }
  };

  const fetchAndShowQuote = async () => {
    // Only show quote once per login session
    if (sessionStorage.getItem('quote_shown_this_session')) return;
    try {
      const quoteRes = await fetch(`${API}/quotes/random`);
      if (quoteRes.ok) {
        const quoteData = await quoteRes.json();
        if (quoteData.quote) {
          setCurrentQuote(quoteData.quote);
          setShowQuotePopup(true);
          sessionStorage.setItem('quote_shown_this_session', 'true');
        }
      }
    } catch (err) {
      console.error('Error fetching quote:', err);
    }
  };

  const handleSubmitMood = async () => {
    if (!selectedMood) {
      toast.error('Please select your mood');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/mood/submit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mood: selectedMood,
          note: moodNote,
          period: moodPeriod
        })
      });

      if (res.ok) {
        toast.success(`${moodPeriod === 'morning' ? 'Morning' : 'Afternoon'} mood recorded! ✨`);
        setShowMoodPopup(false);
        setSelectedMood(null);
        setMoodNote('');
        // After submitting mood, show quote
        fetchAndShowQuote();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit mood');
      }
    } catch (err) {
      toast.error('Failed to submit mood');
    } finally {
      setSubmitting(false);
    }
  };

  const currentMoodInfo = moods.find(m => m.key === selectedMood);

  if (!initialized) return null;

  return (
    <>
      {/* Mood Check-in Popup */}
      <Dialog open={showMoodPopup} onOpenChange={() => {}}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md p-0 overflow-hidden" hideClose>
          {/* Header with gradient */}
          <div className="relative bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 p-6 pb-8">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iYSIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVHJhbnNmb3JtPSJyb3RhdGUoNDUpIj48cGF0aCBkPSJNLTEwIDMwaDYwdjJoLTYweiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNhKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')] opacity-50" />
            <div className="relative flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                {moodPeriod === 'morning' ? <Sun size={24} className="text-amber-300" /> : <Moon size={24} className="text-indigo-200" />}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {moodPeriod === 'morning' ? 'Good Morning! ☀️' : 'Good Afternoon! 🌤️'}
                </h2>
                <p className="text-white/70 text-sm">
                  {moodPeriod === 'morning' ? 'How are you starting your day?' : 'How has your day been so far?'}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 -mt-4">
            <div className="bg-[hsl(var(--surface))] rounded-xl p-4 border border-[hsl(var(--border))]">
              {/* Mood selector */}
              <div className="grid grid-cols-5 gap-2 mb-4" data-testid="mood-popup-selector">
                {moods.map((mood) => (
                  <button
                    key={mood.key}
                    data-testid={`popup-mood-${mood.key}`}
                    onClick={() => setSelectedMood(mood.key)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all transform ${
                      selectedMood === mood.key
                        ? `${mood.bg} border-current ${mood.text} scale-105 shadow-lg`
                        : 'border-transparent hover:border-[hsl(var(--border))] hover:scale-102'
                    }`}
                  >
                    <span className={`text-3xl transition-transform ${selectedMood === mood.key ? 'animate-bounce' : ''}`}>{mood.emoji}</span>
                    <span className={`text-[10px] font-medium ${selectedMood === mood.key ? mood.text : 'text-[hsl(var(--text-muted))]'}`}>{mood.label}</span>
                  </button>
                ))}
              </div>

              {selectedMood && (
                <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <Input
                    data-testid="popup-mood-note"
                    value={moodNote}
                    onChange={(e) => setMoodNote(e.target.value)}
                    placeholder="Want to share why? (optional)"
                    className="bg-[hsl(var(--background))] border-[hsl(var(--border))]"
                  />
                </div>
              )}
            </div>

            <Button
              data-testid="popup-submit-mood"
              onClick={handleSubmitMood}
              disabled={!selectedMood || submitting}
              className={`w-full mt-4 h-12 text-base font-semibold bg-gradient-to-r ${currentMoodInfo?.color || 'from-violet-500 to-indigo-600'} text-white shadow-lg transition-all hover:shadow-xl hover:scale-[1.02]`}
            >
              {submitting ? (
                <><Loader2 size={18} className="animate-spin mr-2" /> Submitting...</>
              ) : (
                <>
                  <Heart size={18} className="mr-2" />
                  Submit {moodPeriod === 'morning' ? 'Morning' : 'Afternoon'} Mood
                </>
              )}
            </Button>

            <p className="text-center text-xs text-[hsl(var(--text-muted))] mt-3">
              This check-in helps us understand team wellness 💜
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quote of the Day Popup */}
      <Dialog open={showQuotePopup} onOpenChange={setShowQuotePopup}>
        <DialogContent className="bg-transparent border-0 shadow-none max-w-lg p-0 overflow-visible" hideClose>
          <div className="relative">
            {/* Animated background */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 via-orange-500/20 to-red-500/20 rounded-3xl blur-xl animate-pulse" />
            
            {/* Main content */}
            <div className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-3xl p-1 shadow-2xl shadow-orange-500/30 animate-in zoom-in-95 duration-500">
              <div className="bg-[hsl(var(--card))] rounded-[22px] p-8 relative overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-orange-500/10 to-transparent rounded-full translate-y-1/2 -translate-x-1/2" />
                
                {/* Close button */}
                <button
                  onClick={() => setShowQuotePopup(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] flex items-center justify-center text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] transition-colors z-10"
                >
                  <X size={16} />
                </button>

                {/* Quote icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30 animate-in spin-in-90 duration-700">
                    <Quote size={32} className="text-white" />
                  </div>
                </div>

                {/* Quote content */}
                {currentQuote ? (
                  <div className="relative animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
                    <Sparkles size={20} className="absolute -top-2 -left-2 text-amber-400 animate-pulse" />
                    <Sparkles size={16} className="absolute -bottom-2 -right-2 text-orange-400 animate-pulse delay-300" />
                    
                    <blockquote className="text-center">
                      <p className="text-xl lg:text-2xl font-medium text-[hsl(var(--text-primary))] leading-relaxed mb-4 italic">
                        "{currentQuote.quote}"
                      </p>
                      <footer className="text-sm text-[hsl(var(--text-muted))]">
                        — {currentQuote.user_name}
                      </footer>
                    </blockquote>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Sparkles size={32} className="mx-auto text-amber-400 mb-2" />
                    <p className="text-[hsl(var(--text-muted))]">No quotes yet today</p>
                  </div>
                )}

                <div className="flex justify-center mt-6 animate-in fade-in duration-700 delay-500">
                  <Button
                    onClick={() => setShowQuotePopup(false)}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 shadow-lg shadow-amber-500/20 hover:shadow-xl hover:scale-105 transition-all"
                  >
                    <Sparkles size={16} className="mr-2" />
                    Start My Day!
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
